import { AnetCommonService } from './anet-common.service';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, catchError, from, mergeMap, Observable, of, Subject, tap, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { environment } from '../../../../environments/environment';
import { AthleticUser } from '../models/athletic-user.interface';
import { AnonymousModel } from '../models/anonymous.model';
import { AthleticFollowInfo } from '../models/athletic-follow-info.interface';
import { AnetLiveService } from '../services/anet-live.service';
import { Notice } from '../../models/notice/notice.interface';
import { AliveNoticeService } from '../../services/alive-notice.service';
import { DeliveryMethod } from '../../models/notice/delivery-method.interface';
import { AliveFollowService } from '../../services/alive-follow.service';
import { MeetList } from '@athleticnet/athletic-live-api-models';
import { jwtDecode } from 'jwt-decode';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly ACCESS_TOKEN = 'ACCESS_TOKEN';
  private readonly REFRESH_TOKEN = 'REFRESH_TOKEN';
  currentFollowInfo: AthleticFollowInfo | null = null;

  private _updateUser: BehaviorSubject<AthleticUser | null> = new BehaviorSubject<AthleticUser | null>(null);
  public readonly updateUser: Observable<AthleticUser | null> = this._updateUser.asObservable();

  private _updateFollowInfo: BehaviorSubject<AthleticFollowInfo | null> =
    new BehaviorSubject<AthleticFollowInfo | null>(null);
  public readonly updateFollowInfo: Observable<AthleticFollowInfo | null> = this._updateFollowInfo.asObservable();

  private _updateNotices: BehaviorSubject<Notice[]> = new BehaviorSubject<Notice[]>([]);
  public readonly updateNotices: Observable<Notice[]> = this._updateNotices.asObservable();

  private _updateDeliveryMethods: BehaviorSubject<DeliveryMethod[]> = new BehaviorSubject<DeliveryMethod[]>([]);
  public readonly updateDeliveryMethods: Observable<DeliveryMethod[]> = this._updateDeliveryMethods.asObservable();

  private _signedIn = new BehaviorSubject<boolean>(false);
  signedIn$ = this._signedIn.asObservable();

  private _signingOut = new BehaviorSubject<boolean>(false);
  signingOut$ = this._signingOut.asObservable();

  private _newUserData = new Subject<any>();
  newUserData$ = this._newUserData.asObservable();

  constructor(
    private common: AnetCommonService,
    private http: HttpClient,
    private router: Router,
    private cookieService: CookieService,
    private anetLiveService: AnetLiveService,
    private aliveFollowService: AliveFollowService,
    private aliveNoticeService: AliveNoticeService,
  ) {}

  async signOut() {
    await this.removeTokens();
    this._signingOut.next(true);
    this._updateUser.next(new AnonymousModel());
    //
    // setTimeout(() => { // slight delay for Cypress tests
    //   this.router.navigate(['login']);
    // }, 50);
  }
  async setUser() {
    const data = await this.anetLiveService.getUserData();
    if (data) {
      this._updateUser.next(data);
    } else {
      this._updateUser.next(new AnonymousModel());
    }
  }

  async setFollowInfo(meet: MeetList) {
    let athleticNetIdArr: any[] = [];
    if (meet.athleticNetId) {
      athleticNetIdArr = [meet.athleticNetId];
    }
    if (athleticNetIdArr[0] === -1) {
      athleticNetIdArr = meet.athleticNetMeets.map((anetMeet: any) => {
        return anetMeet.athleticNetId;
      });
    }

    let anetFollowInfo = await this.anetLiveService.getFollowInfo(meet.sport, athleticNetIdArr.join(','));
    anetFollowInfo = await this.aliveFollowService.addAliveIdsToAnetFollows(meet.id, anetFollowInfo);
    let aliveFollowInfo = await this.aliveFollowService.getFollows(meet.id);
    if (anetFollowInfo) {
      anetFollowInfo.aliveFollows = aliveFollowInfo;
    } else {
      anetFollowInfo = {
        athletes: [],
        teamFollowType: '',
        teams: [],
        aliveFollows: aliveFollowInfo,
      };
    }
    if (this.hasNewFollowInfo(anetFollowInfo)) {
      this.currentFollowInfo = anetFollowInfo;
      this._updateFollowInfo.next(anetFollowInfo);
    }
  }

  protected hasNewFollowInfo(anetFollowInfo: AthleticFollowInfo | null) {
    if (anetFollowInfo === null && this.currentFollowInfo !== null) {
      return true;
    }
    if (this.currentFollowInfo === null && anetFollowInfo !== null) {
      return true;
    }
    return (
      anetFollowInfo &&
      this.currentFollowInfo &&
      !(
        anetFollowInfo.athletes.length === this.currentFollowInfo.athletes.length &&
        anetFollowInfo.teams.length === this.currentFollowInfo.teams.length &&
        anetFollowInfo.aliveFollows.length === this.currentFollowInfo.aliveFollows.length
      )
    );
  }

  async setNotices(meetId: number) {
    const data = await this.aliveNoticeService.getNotices(meetId);
    if (data) {
      this._updateNotices.next(data);
    } else {
      this._updateNotices.next([]);
    }
  }

  async setDeliveryMethods() {
    const data = await this.aliveNoticeService.getDeliveryMethods();
    if (data) {
      this._updateDeliveryMethods.next(data);
    } else {
      this._updateDeliveryMethods.next([]);
    }
  }

  GetNewAccessToken(): Observable<string> {
    //console.log('GetNewAccessToken');
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      //console.log('no refresh token');
      this._updateUser.next(new AnonymousModel());
      return of('');
    }
    return of(refreshToken).pipe(
      mergeMap((refreshToken) => {
        //console.log('start getting the token');
        return this.http
          .post<any>(`${environment.anetAPIEndpoint}/v1/AccountToken/GetAccessToken`, {
            refresh: refreshToken,
          })
          .pipe(
            catchError((error) => {
              if (error instanceof HttpErrorResponse && (error.status === 400 || error.status === 403)) {
                return from(this.signOut());
              } else {
                return throwError(error);
              }
            }),
            tap((data) => this.storeAccessToken(data)),
            tap((data) => this.setUser()),
            tap((data) => this.setDeliveryMethods()),
          );
      }),
    );
  }

  getAccessToken(): string {
    return this.cookieService.get(this.ACCESS_TOKEN);
  }

  getRefreshToken(): string {
    return this.cookieService.get(this.REFRESH_TOKEN);
  }

  private async removeItem(key: string) {
    await this.cookieService.delete(key);
  }

  private storeAccessToken(accessToken: string): void {
    return this.cookieService.set(this.ACCESS_TOKEN, accessToken, undefined, '/');
  }

  public storeRefreshToken(refreshToken: string): void {
    // Expire cookie 10 seconds before the refresh token actually expires
    // Chrome only allows cookies to be set with a max expiration of 400 days, but that limit is handled automatically
    try {
      const { exp } = jwtDecode(refreshToken);
      const expireDate = exp ? new Date((exp - 10) * 1000) : undefined;
      return this.cookieService.set(this.REFRESH_TOKEN, refreshToken, expireDate, '/');
    } catch (e) {
      return this.cookieService.set(this.REFRESH_TOKEN, refreshToken, undefined, '/');
    }
  }

  private storeTokens(tokens: any): Promise<void> {
    this.storeAccessToken(tokens.accessToken);
    this.storeRefreshToken(tokens.refreshToken);
    return Promise.resolve();
  }

  private removeTokens(): Promise<void> {
    this.cookieService.delete(this.ACCESS_TOKEN, '/');
    this.cookieService.delete(this.REFRESH_TOKEN, '/');
    return Promise.resolve();
  }

  //Return a list of roles assigned to the currently logged in user
  getMyRoles(): Promise<string[]> {
    return this.common
      .httpGet(environment.baseAPIEndpoint + '/accounts/roles', {
        insecure: false,
        rejectUnauthorized: true,
      })
      .then((roles: any) => {
        return roles.roles;
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  }
}
